import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardActions,
  CardContent,
  Typography,
  Button,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { API_BASE_URL } from 'src/apiConfig';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { useTranslation } from 'react-i18next';
import { Edit, Delete } from '@mui/icons-material';

/* ADMIN STRANA ZA KATEGORIJE */
const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', notes: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories.php`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditCategory = async () => {
    try {
      if (editingCategory) {
        // Edit existing category
        await axios.put(
          `${API_BASE_URL}/api/categories.php?id=${editingCategory.id}`,
          {
            ...newCategory,
            id: editingCategory.id
          }
        );
      } else {
        // Add new category
        await axios.post(`${API_BASE_URL}/api/categories.php`, {
          name: newCategory.name,
          notes: newCategory.notes
        });
      }
      fetchCategories();
      handleCloseDialog();
    } catch (error) {
      console.error('Error adding/editing category:', error);
    }
  };

  const handleDeleteCategory = async () => {
    try {
      if (categoryToDelete) {
        await axios.delete(
          `${API_BASE_URL}/api/categories.php?id=${categoryToDelete}`,
          {
            data: { id: categoryToDelete }
          }
        );
        fetchCategories();
        handleCloseDeleteDialog();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleOpenDialog = (category = null) => {
    setEditingCategory(category);
    if (category) {
      setNewCategory({
        name: category.name,
        notes: category.notes,
        id: category.id // Ovde se postavlja id za uređivanje
      });
    } else {
      setNewCategory({ name: '', notes: '', id: null }); // Novi unos nema id
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setNewCategory({ name: '', notes: '' });
  };

  const handleOpenDeleteDialog = (id) => {
    setCategoryToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.notes.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box
      style={{ padding: '20px' }}
      sx={{
        padding: 3,
        backgroundColor: '#f5f5f5',
        borderRadius: '15px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '1800px', // Širi boks
        height: '800px'
      }}
    >
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#2596be',
          marginBottom: '20px',
          borderRadius: '10px'
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('categories.title')}
          </Typography>
          <div style={{ position: 'relative' }}>
            <InputBase
              placeholder={t('categories.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputProps={{ 'aria-label': 'search' }}
              sx={{
                color: 'white',
                padding: '0 10px',
                border: '1px solid white',
                borderRadius: '4px'
              }}
            />
            <IconButton sx={{ p: 1, color: 'white' }}>
              <SearchIcon />
            </IconButton>
            <Button
              startIcon={<AddIcon />}
              sx={{
                marginLeft: '20px',
                backgroundColor: 'white',
                color: '#2596be'
              }}
              onClick={() => handleOpenDialog()}
            >
              {t('categories.addNew')}
            </Button>
          </div>
        </Toolbar>
      </AppBar>

      <Grid container spacing={3}>
        {filteredCategories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card
              sx={{
                boxShadow: 3,
                borderRadius: '15px',
                backgroundColor: '#e3f2fd'
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{ color: '#1976d2', fontWeight: 'bold' }}
                >
                  {category.name}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '10px' }}>
                  {category.notes}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="primary"
                  sx={{
                    color: '#1c75bc'
                  }}
                  startIcon={
                    <Edit
                      sx={{
                        color: '#1c75bc',
                        fontSize: '24px'
                      }}
                    />
                  }
                  onClick={() => handleOpenDialog(category)}
                >
                  {t('categories.edit')}
                </Button>
                <Button
                  size="small"
                  color="secondary"
                  sx={{
                    color: '#e91e63' // Boja teksta
                  }}
                  startIcon={
                    <Delete
                      sx={{
                        color: '#e91e63',
                        fontSize: '24px'
                      }}
                    />
                  }
                  onClick={() => handleOpenDeleteDialog(category.id)}
                >
                  {t('categories.delete')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {editingCategory
            ? t('categories.editTitle')
            : t('categories.addTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('categories.nameLabel')}
            type="text"
            fullWidth
            value={newCategory.name}
            onChange={(e) =>
              setNewCategory({ ...newCategory, name: e.target.value })
            }
            variant="outlined"
          />
          <TextField
            margin="dense"
            label={t('categories.notesLabel')}
            type="text"
            fullWidth
            value={newCategory.notes}
            onChange={(e) =>
              setNewCategory({ ...newCategory, notes: e.target.value })
            }
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {t('categories.cancel')}
          </Button>
          <Button onClick={handleAddOrEditCategory} color="secondary">
            {t('categories.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteCategory}
      />
    </Box>
  );
};

export default CategoriesPage;
